import { Loader2, Play } from 'lucide-react';
import MarketingDashboardFilters from './MarketingDashboardFilters';
import MarketingTrendChart from './MarketingTrendChart';
import UnifiedFunnelChart from './UnifiedFunnelChart';
import FunnelTrendChart from './FunnelTrendChart';
import EfficiencyTrendChart from './EfficiencyTrendChart';
import MessagingOverviewChart from './MessagingOverviewChart';
import RevenueBreakdown from '../revenue/RevenueBreakdown';
import { useMarketingStore } from '../../store/marketing.store';

interface Props {
  isDark: boolean;
  theme: Record<string, string>;
}

export default function MarketingDashboardContent({ isDark }: Props) {
  const { isLoading, error, reportFrom, reportTo, fetchedKey, byCampaign, byAccount, byActionType } = useMarketingStore();

  const hasFetched = fetchedKey !== null;

  return (
    <>
      <MarketingDashboardFilters isDark={isDark} />

      {error && (
        <div className={`mt-4 p-4 rounded-lg border ${isDark ? 'bg-red-500/10 border-red-500/30 text-red-300' : 'bg-red-50 border-red-200 text-red-700'}`}>
          {error}
        </div>
      )}

      {isLoading && (
        <div className="flex items-center justify-center py-20">
          <Loader2 className="w-8 h-8 text-cyan-500 animate-spin" />
        </div>
      )}

      {!isLoading && !hasFetched && (
        <div className="mt-8 flex flex-col items-center justify-center py-20 text-center">
          <Play className={`w-12 h-12 ${isDark ? 'text-slate-600' : 'text-gray-400'} mb-4`} />
          <p className={`text-lg ${isDark ? 'text-slate-400' : 'text-gray-500'}`}>Chọn khoảng thời gian và nhấn <span className="text-cyan-400 font-medium">"Chạy report"</span> để xem dữ liệu</p>
        </div>
      )}

      {!isLoading && hasFetched && (
        <div className="mt-4 sm:mt-6 space-y-4 sm:space-y-6">
          <h2 className={`text-xl sm:text-2xl font-bold ${isDark ? 'text-white' : 'text-gray-900'}`}>
            Marketing Dashboard <span className={`text-base font-normal ${isDark ? 'text-cyan-400/80' : 'text-cyan-600'}`}>({reportFrom} — {reportTo})</span>
          </h2>

          {/* Unified Funnel — full width */}
          <UnifiedFunnelChart isDark={isDark} />

          {/* Messaging — ngay sau phễu thống nhất */}
          <MessagingOverviewChart isDark={isDark} />

          {/* Funnel Trend — full width */}
          <FunnelTrendChart isDark={isDark} />

          {/* Trend — full width */}
          <MarketingTrendChart isDark={isDark} />

          {/* Efficiency Trend — full width */}
          <EfficiencyTrendChart isDark={isDark} />

          {/* By Campaign — full width */}
          <RevenueBreakdown
            isDark={isDark}
            title="Chi phí theo Campaign"
            tooltipText={"Chi phí quảng cáo gộp theo campaign.\n\nDoanh thu = SUM(spend) — tổng chi phí quảng cáo.\nLeads (Facebook) = số người gửi form đăng ký trên quảng cáo.\n\nLưu ý: Đây là Facebook leads, KHÁC với HubSpot leads ở phễu phía trên.\n\nMeta API: Ad Insights API → level=campaign"}
            data={byCampaign}
            reportFrom={reportFrom}
            reportTo={reportTo}
            csvFileName="marketing_by_campaign"
            orderCountLabel="Leads (FB)"
          />

          {/* By Account + By Action — 2 cols */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 sm:gap-6">
            <RevenueBreakdown
              isDark={isDark}
              title="Chi phí theo Account"
              tooltipText={"So sánh chi phí giữa các ad accounts.\n\nDoanh thu = SUM(spend) theo từng account.\nLeads (Facebook) = số người gửi form đăng ký.\n\nAccounts: 1A, 2A\nMeta API: Ad Insights API → account level"}
              data={byAccount}
              reportFrom={reportFrom}
              reportTo={reportTo}
              csvFileName="marketing_by_account"
              orderCountLabel="Leads (FB)"
            />
            <RevenueBreakdown
              isDark={isDark}
              title="Phân bổ theo Action"
              tooltipText={"Phân bổ hành động người dùng từ quảng cáo.\n\nSố lượng = tổng số lần action xảy ra.\nBao gồm: Lead, Tin nhắn, Click liên kết, Xem video, Tương tác trang, Thanh toán, v.v.\n\nMeta API field: actions[action_type=*]\nMỗi action_type được đếm riêng và xếp theo số lượng giảm dần."}
              data={byActionType}
              reportFrom={reportFrom}
              reportTo={reportTo}
              csvFileName="marketing_by_action"
              hideRevenue
              orderCountLabel="Số lượng"
            />
          </div>

        </div>
      )}
    </>
  );
}
